// src/contexts/AuthContext.tsx
// Context para manejar la autenticación y sesión del usuario

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { config, js } from '../config/env';
import { apiService } from '../services/api';
import { clearViewCacheForLogout } from '../lib/viewCache';

// ==================== Tipos ====================

export interface ExternalAuthData {
  provider: string;
  userName: string;
  externalAccessToken: string;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  userName?: string;
  givenName?: string;
  personId?: string;
  layerId?: string;
  profileImageName?: string;
  groupId?: string;
  groupName?: string;
  nGroups?: number;
}

export interface UserData {
  access_token: string;
  refresh_token: string;
  username: string;
  givenname: string;
  personid: string;
  layerid: string;
  imageprofile: string;
  groupId: string;
  groupName: string;
  defaultGroupId: string;
  defaultGroupName: string;
  nGroups: string;
  isAdmin: boolean;
  isAdminProject: boolean;
  isBeta: boolean;
  isBetaPermission: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canTemporary: boolean;
  PTOApprover: boolean;
  HRApprover: boolean;
  enableChat: boolean;
  enableInternalChat: boolean;
  initialCustomize: boolean;
  isChangePasswordConfig: boolean;
  ExternalLogin: boolean;
  External: boolean;
  SupportPermissions: boolean;
  EmailAddress: string;
}

export interface PasswordValidationResult {
  ResultId: number; // 1 success, 2 error
  Message: string;
}

export interface PasswordModel {
  userName?: string;
  password: string;
  password2: string;
}

export interface LayerColors {
  [layerId: string]: string;
}

export interface PersonData {
  isAdmin: boolean;
  isBeta: boolean;
  isBetaPermission: boolean;
  isAdminProject: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canTemporary: boolean;
  ExternalLogin: boolean;
  External: boolean;
  SupportPermissions: boolean;
  EmailAddress: string;
}

interface AuthContextType {
  // Estado
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UserData | null;
  error: string | null;

  // Métodos de autenticación
  login: (usernameOrEmailAddress: string, password: string, tenancyName?: string) => Promise<void>;
  logout: () => void;
  refreshAccessToken: (tenancyName?: string) => Promise<TokenResponse>;
  obtainExternalAccessToken: (externalData: ExternalAuthData) => Promise<TokenResponse>;

  // Métodos de gestión de usuario
  setLayer: (layerId: string) => Promise<void>;
  validateUsername: (userName: string) => Promise<any>;
  changePassword: (model: {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => Promise<any>;
  validatePassword: (
    validateUserNameRules: boolean,
    passwordModel: PasswordModel
  ) => PasswordValidationResult;
  setImageProfile: (urlImageProfile: string) => void;
  saveUrl: () => void;

  // Métodos de permisos
  hasItemCreationPermission: () => Promise<boolean>;
  GetPersonLayersById: (personId: string) => Promise<any>;

  // Getters de datos de usuario (compatibilidad con legacy)
  UserName: () => string;
  PersonId: () => string;
  AccessToken: () => string;
  GivenName: () => string;
  LayerId: () => string;
  ImageProfile: () => string;
  defaultGroupId: () => string;
  defaultGroupName: () => string;
  nGroups: () => string;
  IsAdmin: () => boolean;
  IsAdminProject: () => boolean;
  isBeta: () => boolean;
  isBetaME: () => boolean;
  isBetaPermission: () => boolean;
  PTOApprover: () => boolean;
  setPTOApprover: (value: boolean) => void;
  HRApprover: () => boolean;
  setHRApprover: (value: boolean) => void;
  CanEdit: () => boolean;
  CanDelete: () => boolean;
  CanTemporary: () => boolean;
  EnableChat: () => boolean;
  EnableInternalChat: () => boolean;
  InitialCustomize: () => boolean;
  IsChangePasswordConfig: () => string | null;
  UserExternal: () => boolean;
  ExternalLogin: () => boolean;
  SupportPermissions: () => boolean;
  EmailAddress: () => string;
}

// ==================== Constantes ====================

const APP_PREFIX = 'C2';
const STORAGE_KEYS = {
  ACCESS_TOKEN: `${APP_PREFIX}_access_token`,
  REFRESH_TOKEN: `${APP_PREFIX}_refresh_token`,
  USERNAME: `${APP_PREFIX}_username`,
  GIVENNAME: `${APP_PREFIX}_givenname`,
  PERSONID: `${APP_PREFIX}_personid`,
  LAYERID: `${APP_PREFIX}_layerid`,
  IMAGEPROFILE: `${APP_PREFIX}_imageprofile`,
  GROUP_ID: `${APP_PREFIX}_groupId`,
  GROUP_NAME: `${APP_PREFIX}_groupName`,
  DEFAULT_GROUP_ID: `${APP_PREFIX}_defaultGroupId`,
  DEFAULT_GROUP_NAME: `${APP_PREFIX}_defaultGroupName`,
  N_GROUPS: `${APP_PREFIX}_nGroups`,
  DATA: `${APP_PREFIX}_data`,
  TENANCY_NAME: `${APP_PREFIX}_tenancyName`,
  MAIL_URL: `${APP_PREFIX}_mailUrl`,
  REMEMBER_ME: `${APP_PREFIX}_remember_me`,
  EXTERNAL_DATA: `${APP_PREFIX}_external_data`,
  CONFIG_VALUE_ID: `${APP_PREFIX}_ConfigValueId`,
  AUX_CONFIG_VALUE_ID: `${APP_PREFIX}_auxConfigValueId`,
  ICON_PEOPLE: `${APP_PREFIX}_iconPeople`,
  SIDE_MENU_ITEMS: `${APP_PREFIX}_sideMenuItems`,
  INITIAL_CUSTOMIZE: `${APP_PREFIX}_initialCustomize`,
  ENABLE_CHAT: `${APP_PREFIX}_enableChat`,
  ENABLE_INTERNAL_CHAT: `${APP_PREFIX}_enableInternalChat`,
  OBJ_STORAGE_CONVERSATION: `${APP_PREFIX}_objStorageConversation`,
  PTO_APPROVER: `${APP_PREFIX}_PTOApprover`,
  HR_APPROVER: `${APP_PREFIX}_HRApprover`,
  IS_BETA: `${APP_PREFIX}_isBeta`,
  IS_BETA_ME: `${APP_PREFIX}_isBetaME`,
  CHANGE_PASSWORD_CONFIG: `${APP_PREFIX}_changePasswordConfig`,
  HAS_ITEM_CREATION_PERMISSION: `${APP_PREFIX}_hasItemCreationPermission`,
  TENANT_GROUP_ID: `${APP_PREFIX}_tenantGroupId`,
} as const;

// ==================== Context ====================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ==================== Hook personalizado ====================

export const useAuth = () =>
{
  const context = useContext(AuthContext);
  if (!context)
  {
    throw new Error('useAuth debe ser usado dentro de AuthProvider');
  }
  return context;
};

// ==================== Provider ====================

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) =>
{
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<UserData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ==================== Utilidades de almacenamiento ====================

  const getStorageItem = useCallback((key: string): string | null =>
  {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(key);
  }, []);

  const setStorageItem = useCallback((key: string, value: string): void =>
  {
    if (typeof window === 'undefined') return;
    localStorage.setItem(key, value);
  }, []);

  const getSessionStorageItem = useCallback((key: string): string | null =>
  {
    if (typeof window === 'undefined') return null;
    return sessionStorage.getItem(key);
  }, []);

  // ==================== Métodos de autenticación ====================

  /**
   * First-login flags returned by the API: "initialCustomize" is false until the
   * initial wizard has been completed, and "changePasswordConfig" carries the
   * reset token used to force a password change on the first login.
   */
  const persistFirstLoginFlags = useCallback(
    (response: { initialCustomize?: unknown; changePasswordConfig?: unknown }) =>
    {
      if (response.initialCustomize !== undefined && response.initialCustomize !== null)
      {
        setStorageItem(
          STORAGE_KEYS.INITIAL_CUSTOMIZE,
          String(response.initialCustomize).toLowerCase() === 'true' ? 'true' : 'false',
        );
      }
      else
      {
        setStorageItem(STORAGE_KEYS.INITIAL_CUSTOMIZE, 'true');
      }

      if (response.changePasswordConfig && typeof response.changePasswordConfig === 'object')
      {
        setStorageItem(
          STORAGE_KEYS.CHANGE_PASSWORD_CONFIG,
          JSON.stringify(response.changePasswordConfig),
        );
      }
      else
      {
        localStorage.removeItem(STORAGE_KEYS.CHANGE_PASSWORD_CONFIG);
      }
    },
    [setStorageItem],
  );

  const login = useCallback(
    async (usernameOrEmailAddress: string, password: string, tenancyName?: string) =>
    {
      setIsLoading(true);
      setError(null);

      try
      {
        const response = (await apiService.Authenticate(
          usernameOrEmailAddress,
          password,
          tenancyName,
        )) as any;

        // A rejected login answers with a bare JSON string holding the reason
        if (typeof response === 'string')
        {
          throw new Error(response);
        }

        const accessToken = response.access_token || response.accessToken || '';
        if (!accessToken)
        {
          throw new Error('Invalid credentials');
        }

        const tenantName = tenancyName || config.tenant.defaultName;

        // Guardar tokens
        setStorageItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
        setStorageItem(
          STORAGE_KEYS.REFRESH_TOKEN,
          response.refresh_token || response.refreshToken || '',
        );
        setStorageItem(STORAGE_KEYS.TENANCY_NAME, tenantName);

        // Guardar datos de usuario
        if (response.userName) setStorageItem(STORAGE_KEYS.USERNAME, response.userName);
        if (response.givenName) setStorageItem(STORAGE_KEYS.GIVENNAME, response.givenName);
        if (response.personId) setStorageItem(STORAGE_KEYS.PERSONID, response.personId);
        if (response.layerId) setStorageItem(STORAGE_KEYS.LAYERID, response.layerId);
        if (response.profileImageName)
          setStorageItem(STORAGE_KEYS.IMAGEPROFILE, response.profileImageName);
        if (response.groupId)
        {
          setStorageItem(STORAGE_KEYS.GROUP_ID, response.groupId);
          setStorageItem(STORAGE_KEYS.DEFAULT_GROUP_ID, response.groupId);
        }
        if (response.groupName)
        {
          setStorageItem(STORAGE_KEYS.GROUP_NAME, response.groupName);
          setStorageItem(STORAGE_KEYS.DEFAULT_GROUP_NAME, response.groupName);
        }
        if (response.nGroups !== undefined)
          setStorageItem(STORAGE_KEYS.N_GROUPS, String(response.nGroups));
        if (response.tenantGroupId !== undefined)
          setStorageItem(STORAGE_KEYS.TENANT_GROUP_ID, String(response.tenantGroupId));
        if (response.enableChat !== undefined)
          setStorageItem(STORAGE_KEYS.ENABLE_CHAT, String(response.enableChat));
        if (response.enableInternalChat !== undefined)
          setStorageItem(STORAGE_KEYS.ENABLE_INTERNAL_CHAT, String(response.enableInternalChat));
        persistFirstLoginFlags(response);
        // Actualizar estado
        setIsAuthenticated(true);
        setUser({
          access_token: accessToken,
          refresh_token: response.refresh_token || response.refreshToken || '',
          username: response.userName || '',
          givenname: response.givenName || '',
          personid: response.personId || '',
          layerid: response.layerId || '',
          imageprofile: response.profileImageName || '',
          groupId: response.groupId || '',
          groupName: response.groupName || '',
          defaultGroupId: response.groupId || '',
          defaultGroupName: response.groupName || '',
          nGroups: response.nGroups ? String(response.nGroups) : '0',
          isAdmin: false, // Se obtiene de sessionStorage
          isAdminProject: false,
          isBeta: getStorageItem(STORAGE_KEYS.IS_BETA) === 'true',
          isBetaPermission: false,
          canEdit: false,
          canDelete: false,
          canTemporary: false,
          PTOApprover: getStorageItem(STORAGE_KEYS.PTO_APPROVER) === 'true',
          HRApprover: getStorageItem(STORAGE_KEYS.HR_APPROVER) === 'true',
          enableChat: Boolean(response.enableChat),
          enableInternalChat: Boolean(response.enableInternalChat),
          initialCustomize: true,
          isChangePasswordConfig: getStorageItem(STORAGE_KEYS.CHANGE_PASSWORD_CONFIG) === 'true',
          ExternalLogin: false,
          External: false,
          SupportPermissions: false,
          EmailAddress: '',
        });

        // Configurar axios con el token
        (apiService as any)['ensureAccess']?.();
      }
      catch (err)
      {
        const errorMessage = err instanceof Error ? err.message : 'Error al iniciar sesión';
        setError(errorMessage);
        console.error('❌ Login error:', err);
        throw err;
      }
      finally
      {
        setIsLoading(false);
      }
    },
    [setStorageItem, getStorageItem, config, persistFirstLoginFlags],
  );

  const logout = useCallback(() =>
  {
    console.warn('🔐 Logging out...');

    // Purge view cache and block writes from in-flight requests
    clearViewCacheForLogout();

    // Limpiar todas las llaves C2_ de localStorage
    const localKeys = Object.keys(localStorage);
    localKeys.forEach(key =>
    {
      if (key.startsWith(`${APP_PREFIX}_`))
      {
        localStorage.removeItem(key);
      }
    });

    // Limpiar todas las llaves C2_ de sessionStorage
    const sessionKeys = Object.keys(sessionStorage);
    sessionKeys.forEach(key =>
    {
      if (key.startsWith(`${APP_PREFIX}_`))
      {
        sessionStorage.removeItem(key);
      }
    });

    // Resetear estado
    setIsAuthenticated(false);
    setUser(null);
    setError(null);

    // Redirigir al login respetando el base path del ambiente (ej: /V2/)
    window.location.href = import.meta.env.BASE_URL || '/';
  }, []);

  const refreshAccessToken = useCallback(
    async (tenancyName?: string): Promise<TokenResponse> =>
    {
      const refreshToken = getStorageItem(STORAGE_KEYS.REFRESH_TOKEN);
      const tenant = tenancyName || getStorageItem(STORAGE_KEYS.TENANCY_NAME) || 'Default';

      if (!refreshToken)
      {
        throw new Error('No refresh token available');
      }

      const formData = new URLSearchParams();
      formData.append('grant_type', 'refresh_token');
      formData.append('refresh_token', refreshToken);
      formData.append('tenancyName', tenant);
      formData.append('Client_id', 'CorgeeWeb');

      const response = await fetch(`${js.settings.apiAuthURL || config.api.baseUrl}token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (!response.ok)
      {
        logout();
        throw new Error('Failed to refresh token');
      }

      const data = await response.json();

      // Actualizar tokens
      setStorageItem(STORAGE_KEYS.REFRESH_TOKEN, data.refresh_token);
      setStorageItem(STORAGE_KEYS.ACCESS_TOKEN, data.access_token);

      return data;
    },
    [getStorageItem, setStorageItem, logout, js.settings, config],
  );

  const obtainExternalAccessToken = useCallback(
    async (externalData: ExternalAuthData): Promise<TokenResponse> =>
    {
      // El backend espera estos nombres de parámetros (según código legacy)
      const queryParams = new URLSearchParams({
        loginProvider: externalData.provider, // "loginProvider" no "provider"
        providerKey: externalData.externalAccessToken, // "providerKey" no "externalAccessToken"
        tenancyName: getStorageItem(STORAGE_KEYS.TENANCY_NAME) || config.tenant.defaultName, // tenancyName es requerido
      });

      // _retry: true evita que el interceptor de 401 haga refresh/logout
      // Esta llamada ocurre antes de tener tokens, por lo que un 401 es un error de negocio (ej. cuenta deshabilitada)
      const response = await apiService.get<TokenResponse>(
        `account/ObtainLocalAccessToken?${queryParams.toString()}`,
        { _retry: true } as any,
      );

      // Guardar tokens y datos de usuario
      setStorageItem(STORAGE_KEYS.REFRESH_TOKEN, response.refresh_token);
      setStorageItem(STORAGE_KEYS.ACCESS_TOKEN, response.access_token);
      if (response.userName) setStorageItem(STORAGE_KEYS.USERNAME, response.userName);
      if (response.givenName) setStorageItem(STORAGE_KEYS.GIVENNAME, response.givenName);
      if (response.personId) setStorageItem(STORAGE_KEYS.PERSONID, response.personId);
      if (response.layerId) setStorageItem(STORAGE_KEYS.LAYERID, response.layerId);
      if (response.profileImageName)
        setStorageItem(STORAGE_KEYS.IMAGEPROFILE, response.profileImageName);
      if (response.groupId)
      {
        setStorageItem(STORAGE_KEYS.GROUP_ID, response.groupId);
        setStorageItem(STORAGE_KEYS.DEFAULT_GROUP_ID, response.groupId);
      }
      if (response.groupName)
      {
        setStorageItem(STORAGE_KEYS.GROUP_NAME, response.groupName);
        setStorageItem(STORAGE_KEYS.DEFAULT_GROUP_NAME, response.groupName);
      }
      if (response.nGroups !== undefined)
        setStorageItem(STORAGE_KEYS.N_GROUPS, String(response.nGroups));
      persistFirstLoginFlags(response as any);

      // Actualizar estado INMEDIATAMENTE
      setIsAuthenticated(true);
      (apiService as any)['ensureAccess']?.();

      return response;
    },
    [setStorageItem, persistFirstLoginFlags],
  );

  // ==================== Métodos de gestión de usuario ====================

  const setLayer = useCallback(
    async (layerId: string): Promise<void> =>
    {
      const response = (await apiService.post('person/UpdateDefaultLayer', {
        LayerId: layerId,
      })) as any;
      setStorageItem(STORAGE_KEYS.LAYERID, response.layerId);
      if (user)
      {
        setUser({ ...user, layerid: response.layerId });
      }
    },
    [setStorageItem, user],
  );

  const validateUsername = useCallback(async (userName: string): Promise<any> =>
  {
    return await apiService.get('account/ValidateUserName', { params: { userName } });
  }, []);

  const changePassword = useCallback(
    async (model: {
      oldPassword: string;
      newPassword: string;
      confirmPassword: string;
    }): Promise<any> =>
    {
      return await apiService.post('account/ChangePassword', model);
    },
    [],
  );

  const validatePassword = useCallback(
    (validateUserNameRules: boolean, passwordModel: PasswordModel): PasswordValidationResult =>
    {
      const result: PasswordValidationResult = {
        ResultId: 1,
        Message: 'Password is correct.',
      };

      if (validateUserNameRules && passwordModel.userName === '')
      {
        result.ResultId = 2;
        result.Message = 'User name cannot be blank.';
        return result;
      }

      const hasNumber = /[0-9]/;
      const hasLower = /[a-z]/;
      const hasUpper = /[A-Z]/;
      const hasSpace = /\ /;

      if (passwordModel.password !== '' && passwordModel.password === passwordModel.password2)
      {
        if (passwordModel.password.length < 6)
        {
          result.ResultId = 2;
          result.Message = 'Password must contain at least six characters.';
          return result;
        }
        if (passwordModel.password.length > 16)
        {
          result.ResultId = 2;
          result.Message = 'Password length allows sixteen characters maximum.';
          return result;
        }
        if (validateUserNameRules && passwordModel.password === passwordModel.userName)
        {
          result.ResultId = 2;
          result.Message = 'Password must be different from the Account Name.';
          return result;
        }
        if (!hasNumber.test(passwordModel.password))
        {
          result.ResultId = 2;
          result.Message = 'Password must contain at least one number (0-9).';
          return result;
        }
        if (!hasLower.test(passwordModel.password))
        {
          result.ResultId = 2;
          result.Message = 'Password must contain at least one lowercase letter (a-z).';
          return result;
        }
        if (!hasUpper.test(passwordModel.password))
        {
          result.ResultId = 2;
          result.Message = 'Password must contain at least one uppercase letter (A-Z).';
          return result;
        }
        if (hasSpace.test(passwordModel.password))
        {
          result.ResultId = 2;
          result.Message = 'Password can not contain blank spaces.';
          return result;
        }
      }
      else
      {
        if (passwordModel.password === '' || passwordModel.password2 === '')
        {
          result.ResultId = 2;
          result.Message =
            "Please check that you've entered and confirmed your password correctly.";
          return result;
        }
        if (passwordModel.password !== passwordModel.password2)
        {
          result.ResultId = 2;
          result.Message = 'New password and confirmed password does not match, please verify.';
          return result;
        }
      }

      return result;
    },
    [],
  );

  const setImageProfile = useCallback(
    (urlImageProfile: string): void =>
    {
      setStorageItem(STORAGE_KEYS.IMAGEPROFILE, urlImageProfile);
    },
    [setStorageItem],
  );

  const saveUrl = useCallback((): void =>
  {
    setStorageItem(STORAGE_KEYS.MAIL_URL, window.location.href);
  }, [setStorageItem]);

  // ==================== Métodos de permisos ====================

  const hasItemCreationPermission = useCallback(async (): Promise<boolean> =>
  {
    try
    {
      const result = (await apiService.get('Security/HasItemCreationPermission')) as any;
      const hasPermission = result || false;
      setStorageItem(STORAGE_KEYS.HAS_ITEM_CREATION_PERMISSION, String(hasPermission));
      return !!hasPermission;
    }
    catch
    {
      setStorageItem(STORAGE_KEYS.HAS_ITEM_CREATION_PERMISSION, 'false');
      return false;
    }
  }, [setStorageItem]);

  const GetPersonLayersById = useCallback(async (personId: string): Promise<any> =>
  {
    return await apiService.get('Layer/GetPersonLayersById', { params: { personId } });
  }, []);

  // ==================== Getters de datos de usuario ====================

  const getPersonData = useCallback((): PersonData | null =>
  {
    try
    {
      const data = getSessionStorageItem(STORAGE_KEYS.DATA);
      if (!data) return null;
      return JSON.parse(data);
    }
    catch
    {
      return null;
    }
  }, [getSessionStorageItem]);

  const UserName = useCallback((): string =>
  {
    return getStorageItem(STORAGE_KEYS.USERNAME) || '';
  }, [getStorageItem]);

  const PersonId = useCallback((): string =>
  {
    return getStorageItem(STORAGE_KEYS.PERSONID) || '';
  }, [getStorageItem]);

  const AccessToken = useCallback((): string =>
  {
    return getStorageItem(STORAGE_KEYS.ACCESS_TOKEN) || '';
  }, [getStorageItem]);

  const GivenName = useCallback((): string =>
  {
    return getStorageItem(STORAGE_KEYS.GIVENNAME) || '';
  }, [getStorageItem]);

  const LayerId = useCallback((): string =>
  {
    return getStorageItem(STORAGE_KEYS.LAYERID) || '';
  }, [getStorageItem]);

  const ImageProfile = useCallback((): string =>
  {
    return getStorageItem(STORAGE_KEYS.IMAGEPROFILE) || '';
  }, [getStorageItem]);

  const defaultGroupId = useCallback((): string =>
  {
    return getStorageItem(STORAGE_KEYS.DEFAULT_GROUP_ID) || '';
  }, [getStorageItem]);

  const defaultGroupName = useCallback((): string =>
  {
    return getStorageItem(STORAGE_KEYS.DEFAULT_GROUP_NAME) || '';
  }, [getStorageItem]);

  const nGroups = useCallback((): string =>
  {
    return getStorageItem(STORAGE_KEYS.N_GROUPS) || '0';
  }, [getStorageItem]);

  const IsAdmin = useCallback((): boolean =>
  {
    const personData = getPersonData();
    if (!personData) return false;
    try
    {
      return String(personData.isAdmin).toLowerCase() === 'true';
    }
    catch
    {
      return false;
    }
  }, [getPersonData]);

  const IsAdminProject = useCallback((): boolean =>
  {
    const personData = getPersonData();
    if (!personData) return false;
    try
    {
      return String(personData.isAdminProject).toLowerCase() === 'true';
    }
    catch
    {
      return false;
    }
  }, [getPersonData]);

  const isBeta = useCallback((): boolean =>
  {
    const value = getStorageItem(STORAGE_KEYS.IS_BETA);
    return value === 'true';
  }, [getStorageItem]);

  const isBetaME = useCallback((): boolean =>
  {
    const value = getStorageItem(STORAGE_KEYS.IS_BETA_ME);
    return value === 'true';
  }, [getStorageItem]);

  const isBetaPermission = useCallback((): boolean =>
  {
    const personData = getPersonData();
    if (!personData) return false;
    try
    {
      return String(personData.isBetaPermission).toLowerCase() === 'true';
    }
    catch
    {
      return false;
    }
  }, [getPersonData]);

  const PTOApprover = useCallback((): boolean =>
  {
    const value = getStorageItem(STORAGE_KEYS.PTO_APPROVER);
    return value === 'true';
  }, [getStorageItem]);

  const setPTOApprover = useCallback(
    (value: boolean): void =>
    {
      setStorageItem(STORAGE_KEYS.PTO_APPROVER, String(value));
    },
    [setStorageItem],
  );

  const HRApprover = useCallback((): boolean =>
  {
    const value = getStorageItem(STORAGE_KEYS.HR_APPROVER);
    return value === 'true';
  }, [getStorageItem]);

  const setHRApprover = useCallback(
    (value: boolean): void =>
    {
      setStorageItem(STORAGE_KEYS.HR_APPROVER, String(value));
    },
    [setStorageItem],
  );

  const CanEdit = useCallback((): boolean =>
  {
    const personData = getPersonData();
    if (!personData) return false;
    try
    {
      return String(personData.canEdit).toLowerCase() === 'true';
    }
    catch
    {
      return false;
    }
  }, [getPersonData]);

  const CanDelete = useCallback((): boolean =>
  {
    const personData = getPersonData();
    if (!personData) return false;
    try
    {
      return String(personData.canDelete).toLowerCase() === 'true';
    }
    catch
    {
      return false;
    }
  }, [getPersonData]);

  const CanTemporary = useCallback((): boolean =>
  {
    const personData = getPersonData();
    if (!personData) return false;
    try
    {
      return String(personData.canTemporary).toLowerCase() === 'true';
    }
    catch
    {
      return false;
    }
  }, [getPersonData]);

  const EnableChat = useCallback((): boolean =>
  {
    try
    {
      const value = getStorageItem(STORAGE_KEYS.ENABLE_CHAT);
      return value ? JSON.parse(value) : false;
    }
    catch
    {
      return false;
    }
  }, [getStorageItem]);

  const EnableInternalChat = useCallback((): boolean =>
  {
    try
    {
      const value = getStorageItem(STORAGE_KEYS.ENABLE_INTERNAL_CHAT);
      return value ? JSON.parse(value) : false;
    }
    catch
    {
      return false;
    }
  }, [getStorageItem]);

  const InitialCustomize = useCallback((): boolean =>
  {
    try
    {
      const value = getStorageItem(STORAGE_KEYS.INITIAL_CUSTOMIZE);
      return value ? JSON.parse(value).toString().toLowerCase() === 'true' : true;
    }
    catch
    {
      return true;
    }
  }, [getStorageItem]);

  const IsChangePasswordConfig = useCallback((): string | null =>
  {
    return getStorageItem(STORAGE_KEYS.CHANGE_PASSWORD_CONFIG);
  }, [getStorageItem]);

  const UserExternal = useCallback((): boolean =>
  {
    const personData = getPersonData();
    if (!personData) return false;
    try
    {
      return String(personData.External).toLowerCase() === 'true';
    }
    catch
    {
      return false;
    }
  }, [getPersonData]);

  const ExternalLogin = useCallback((): boolean =>
  {
    const personData = getPersonData();
    if (!personData) return false;
    try
    {
      return String(personData.ExternalLogin).toLowerCase() === 'true';
    }
    catch
    {
      return false;
    }
  }, [getPersonData]);

  const SupportPermissions = useCallback((): boolean =>
  {
    const personData = getPersonData();
    if (!personData) return false;
    try
    {
      return String(personData.SupportPermissions).toLowerCase() === 'true';
    }
    catch
    {
      return false;
    }
  }, [getPersonData]);

  const EmailAddress = useCallback((): string =>
  {
    const personData = getPersonData();
    if (!personData) return '';
    try
    {
      return personData.EmailAddress || '';
    }
    catch
    {
      return '';
    }
  }, [getPersonData]);

  // ==================== Inicialización ====================

  useEffect(() =>
  {
    // Verificar si hay un token guardado
    const accessToken = getStorageItem(STORAGE_KEYS.ACCESS_TOKEN);
    const refreshToken = getStorageItem(STORAGE_KEYS.REFRESH_TOKEN);

    if (accessToken && refreshToken)
    {
      setIsAuthenticated(true);
      setUser({
        access_token: accessToken,
        refresh_token: refreshToken,
        username: getStorageItem(STORAGE_KEYS.USERNAME) || '',
        givenname: getStorageItem(STORAGE_KEYS.GIVENNAME) || '',
        personid: getStorageItem(STORAGE_KEYS.PERSONID) || '',
        layerid: getStorageItem(STORAGE_KEYS.LAYERID) || '',
        imageprofile: getStorageItem(STORAGE_KEYS.IMAGEPROFILE) || '',
        groupId: getStorageItem(STORAGE_KEYS.GROUP_ID) || '',
        groupName: getStorageItem(STORAGE_KEYS.GROUP_NAME) || '',
        defaultGroupId: getStorageItem(STORAGE_KEYS.DEFAULT_GROUP_ID) || '',
        defaultGroupName: getStorageItem(STORAGE_KEYS.DEFAULT_GROUP_NAME) || '',
        nGroups: getStorageItem(STORAGE_KEYS.N_GROUPS) || '0',
        isAdmin: false,
        isAdminProject: false,
        isBeta: isBeta(),
        isBetaPermission: false,
        canEdit: false,
        canDelete: false,
        canTemporary: false,
        PTOApprover: PTOApprover(),
        HRApprover: HRApprover(),
        enableChat: EnableChat(),
        enableInternalChat: EnableInternalChat(),
        initialCustomize: InitialCustomize(),
        isChangePasswordConfig: IsChangePasswordConfig() === 'true',
        ExternalLogin: false,
        External: false,
        SupportPermissions: false,
        EmailAddress: '',
      });
    }

    setIsLoading(false);
  }, []); // Solo se ejecuta una vez al montar

  // ==================== Valor del contexto ====================

  const value: AuthContextType = {
    // Estado
    isAuthenticated,
    isLoading,
    user,
    error,

    // Métodos de autenticación
    login,
    logout,
    refreshAccessToken,
    obtainExternalAccessToken,

    // Métodos de gestión de usuario
    setLayer,
    validateUsername,
    changePassword,
    validatePassword,
    setImageProfile,
    saveUrl,

    // Métodos de permisos
    hasItemCreationPermission,
    GetPersonLayersById,

    // Getters de datos de usuario
    UserName,
    PersonId,
    AccessToken,
    GivenName,
    LayerId,
    ImageProfile,
    defaultGroupId,
    defaultGroupName,
    nGroups,
    IsAdmin,
    IsAdminProject,
    isBeta,
    isBetaME,
    isBetaPermission,
    PTOApprover,
    setPTOApprover,
    HRApprover,
    setHRApprover,
    CanEdit,
    CanDelete,
    CanTemporary,
    EnableChat,
    EnableInternalChat,
    InitialCustomize,
    IsChangePasswordConfig,
    UserExternal,
    ExternalLogin,
    SupportPermissions,
    EmailAddress,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
